import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { FormGroup, ControlContainer, Validators } from '@angular/forms';

// store
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';

// services
import { AppState } from '../../app-reducer';
import { ApiService } from 'src/app/services/api.service';
import { QuestionService } from '../../services/question.service';

import { validateZip, validatePhone } from 'src/app/validators';

@Component({
  selector: 'physician-form',
  templateUrl: './physician-form.component.html',
  styles: []
})

export class PhysicianFormComponent implements OnInit {

  private request: any = null;

  @Input() parent: FormGroup;
  @Input() isRequired: boolean;
  blockIndex = 1;
  mask: any = {};

  // For search input / form control change detection
  SearchInputUpdate = new Subject<any>();

  processing = false;
  err = false;

  public physicianFormGroup: FormGroup;
  formGroupParent: any;

  appState$: Observable<any>;
  states: any;

  queryString: string;

  // Physicians returned by NPPES API
  public PhysiciansDataArray = [];

  // Flag to show list of Physicians
  public showList = false;

  constructor(
    private controlContainer: ControlContainer,
    private questionService: QuestionService,
    private apiService: ApiService,
    private store: Store<AppState>,
    private cdRef: ChangeDetectorRef,
  ) {
    this.appState$ = store.select('sections');

    this.SearchInputUpdate.pipe(
      distinctUntilChanged(),
      debounceTime(355))
      .subscribe(value => {

        if (value) {
          if (this.request) {
            this.hidePhysicianList();
            this.request.unsubscribe();
            this.searchForPhysicians();
          } else {
            this.hidePhysicianList();
            this.searchForPhysicians();
          }
        } else {
          if (this.request) {
            this.hidePhysicianList();
            this.stopAnimation();
            this.request.unsubscribe();
          } else {
            this.hidePhysicianList();
            this.stopAnimation();
          }
        }
      });
  }

  ngOnInit() {
    this.physicianFormGroup = <FormGroup>this.controlContainer.control;
    this.formGroupParent = this.controlContainer.name;

    // activate mask
    this.mask = this.questionService.activateMasks('mask-phone');

    // get US states
    this.appState$.pipe(first()).subscribe((state: any) => {
      this.states = state.formData.states;
    });

    // Grab proposed insured state of residence from app state
    this.appState$.pipe(
      first()
    ).subscribe((state: any) => {
      try {
        this.physicianFormGroup.get('StateFilter').setValue(state['content']['SECTIONAProposedInsured']['PrimaryResidence']['State']);
      } catch (e) {
        console.log('ERROR: ', e);
      }
    });

    if (!this.isRequired) {
      this.physicianFormGroup.controls['Name'].clearValidators();
      this.physicianFormGroup.controls['Phone'].clearValidators();
      this.physicianFormGroup.controls['Phone'].setValidators([Validators.nullValidator,validatePhone]);
      this.physicianFormGroup.controls['Street'].clearValidators();
      this.physicianFormGroup.controls['Street2'].clearValidators();
      this.physicianFormGroup.controls['City'].clearValidators();
      this.physicianFormGroup.controls['State'].clearValidators();
      this.physicianFormGroup.controls['Zip'].clearValidators();
      this.physicianFormGroup.controls['Zip'].setValidators([Validators.nullValidator,validateZip]);
      this.physicianFormGroup.updateValueAndValidity();
    }
  }

  // NPPES Physician Search
  searchForPhysicians() {
    try {
      const searchInput = this.queryString;
      const searchInputLength = this.physicianFormGroup.get('Search').value.toString().length;
      const stateInput = this.physicianFormGroup.get('StateFilter').value;

      // Reset error message flag
      this.err = false;
      // Reset Physician List and start search
      this.resetPhysicianDataArray();

      if (searchInput && !this.isWhiteSpace(searchInput)) {
        // Add wildcard search input to payload if str length > 2
        // add state input to payload
        const payload = searchInputLength <= 1 ? { last_name: searchInput, state: stateInput } : { last_name: searchInput + '*', state: stateInput };

        // Start progress wheel animation
        this.startAnimation();
        // Call service
        this.request = this.apiService.physicianSearch(payload).pipe().subscribe((res) => {
          this.resetPhysicianDataArray();
          this.checkPhysicianDataArrayStatus(res);
          
          // Stop progress wheel animation
          this.stopAnimation();
        });
      } else {
        // Stop progress wheel animation
        this.request = true;
        this.stopAnimation();
      }
    } catch (e) {
      console.log(e);
      this.stopAnimation();
    }
  }

  checkPhysicianDataArrayStatus (res: any) {
    if (res.code === 200 && res.data) {
      this.populatePhysicianDataArray(res.data);

      if (!this.isEmptyArray(this.PhysiciansDataArray)) {
        this.showPhysicianList();
      } else {
        this.hidePhysicianList();
      }
      this.request = null;
    } else if (res.code !== 200) {
      this.err = true;
      this.stopAnimation();
      this.request = null;
    }
  }

  // Helper functions

  // Set form values
  setFormValues(data) {
    this.physicianFormGroup.get('Name').setValue(this.removeSpecialCharacters(data['first_name']) + ' ' + this.removeSpecialCharacters(data['last_name']));
    this.physicianFormGroup.get('City').setValue(data['city']);
    this.physicianFormGroup.get('Street').setValue(data['street']);
    this.physicianFormGroup.get('Street2').setValue(data['street2']);
    this.physicianFormGroup.get('Phone').setValue(data['phone']);
    this.physicianFormGroup.get('Zip').setValue(data['zip']);
    this.physicianFormGroup.get('State').setValue(data['state']);
  }

  // On state drop down selection
  // Perform physician search only if name value is present and state is changed
  onStateChange($event) {
    if (this.physicianFormGroup.get('Search').value) {
      this.searchForPhysicians();
    }
  }

  // Show physician list
  showPhysicianList() {
    this.showList = true;
    this.cdRef.detectChanges();
  }

  // Hide physician list
  hidePhysicianList() {
    this.showList = false;
    this.cdRef.detectChanges();
  }

  // Start wheel animation
  startAnimation() {
    this.processing = true;
    this.cdRef.detectChanges();
  }

  // End wheel animation
  stopAnimation() {
    this.processing = false;
    this.cdRef.detectChanges();
  }

  // Populate physician data array
  populatePhysicianDataArray(data) {
    if (data.length > 1) {
      for (const o of data) {
        if (o.length !== 0) {
          this.PhysiciansDataArray.push(o);
        }
      }
    } else {
      if (data.length !== 0) {
        this.PhysiciansDataArray.push(data);
      }
    }
  }

  // Format physician name
  removeSpecialCharacters(string) {
    return string.replace(/[,;/|()_%@!?.]/g, '');
  }

  // Reset physician data array
  resetPhysicianDataArray() {
    this.PhysiciansDataArray.length = 0;
    this.cdRef.detectChanges();
  }

  // Empty array
  isEmptyArray(array) {
    if (array.length === 1) {
      return array[0] === [];
    }
  }

  // Determines whether or not a string contains only whitespace
  isWhiteSpace(myString) {
    return myString && !myString.trim();
  }

  // address from autocomplete
  updateAddress(event) {
    this.physicianFormGroup.patchValue(event);
  }
}
